import React from "react";
import PropTypes from "prop-types";
import { Alert, Button, Card, FilterLines, SearchLg, Table, TextInput } from "@opendate-app/setlist";
import SaveViewButton from "../shared/components/SaveViewButton";
import TableSettings from "../shared/components/TableSettings";
import FilterSidebar from "../shared/components/FilterSidebar";
import ActiveFilterBadges from "../shared/components/ActiveFilterBadges";
import ClassesTableFooter from "./ClassesTableFooter";
import ArchiveConfirmModal from "./ArchiveConfirmModal";
import { useClassesIndexTableContext } from "./useClassesIndexTable";
import "./classesShared.css";

export default function IndexToolbar() {
  const table = useClassesIndexTableContext();
  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap mt-4 mb-3 classes-gap-12">
        <div className="classes-search-field">
          <TextInput
            aria-label={`Search ${table.noun}`}
            leadingIcon={SearchLg}
            value={table.search}
            onChange={(e) => table.setSearch(e.target.value)}
            placeholder={`Search ${table.noun}...`}
          />
        </div>
        <div className="d-flex align-items-center classes-gap-8">
          <SaveViewButton
            hasChanges={table.hasViewChanges}
            savedViews={table.savedViews}
            currentView={table.currentView}
            onSaveView={table.saveView}
            onLoadView={table.loadView}
            onDeleteView={table.deleteView}
          />
          <div className="position-relative">
            <Button variant="secondary" icon={FilterLines} onClick={() => table.setTableSettingsOpen((o) => !o)} aria-expanded={table.tableSettingsOpen}>
              Table Settings
            </Button>
            <TableSettings
              isOpen={table.tableSettingsOpen}
              onClose={() => table.setTableSettingsOpen(false)}
              visibleFields={table.visibleColumns}
              hiddenFields={table.hiddenColumns}
              onFieldsChange={table.changeColumns}
              sortBy={table.sortBy}
              onSortByChange={table.setSortBy}
              sortOptions={table.sortOptions}
            />
          </div>
        </div>
      </div>

      <div className="mb-3">
        <ActiveFilterBadges
          activeFilters={table.activeFilters}
          filterGroups={table.filterGroups}
          onRemoveFilter={table.removeFilter}
          onClearAll={() => table.setActiveFilters({})}
          onOpenFilterSidebar={() => table.setFilterSidebarOpen(true)}
        />
      </div>

      {table.error && (
        <div className="mb-3">
          <Alert color="error" role="alert" description={table.error} />
        </div>
      )}
    </>
  );
}

export function IndexTable({ columns, ...tableProps }) {
  const table = useClassesIndexTableContext();
  return (
    <Card
      padding="none"
      footer={(
        <ClassesTableFooter
          rangeStart={table.rangeStart}
          rangeEnd={table.rangeEnd}
          total={table.totalEntries}
          noun={table.noun}
          page={table.page}
          totalPages={table.totalPages}
          onPageChange={table.setPage}
        />
      )}
    >
      <Table className="classes-table" columns={columns} data={table.rows} loading={table.loading} getRowKey={(row) => row.id} stackBelow={640} {...tableProps} />
    </Card>
  );
}

IndexTable.propTypes = {
  columns: PropTypes.array.isRequired,
};

export function IndexOverlays() {
  const table = useClassesIndexTableContext();
  return (
    <>
      <FilterSidebar
        isOpen={table.filterSidebarOpen}
        onClose={() => table.setFilterSidebarOpen(false)}
        onApply={() => {}}
        title={`Filter ${table.nounTitle}`}
        subtitle={`Apply filters to find specific ${table.noun}.`}
        filterGroups={table.filterGroups}
        activeFilters={table.activeFilters}
        onFilterChange={table.setActiveFilters}
      />
      {table.pendingArchive && (
        <ArchiveConfirmModal
          kind={table.kind}
          name={table.pendingArchive.name}
          working={table.archiving}
          onConfirm={table.confirmArchive}
          onClose={() => table.setPendingArchive(null)}
        />
      )}
    </>
  );
}
